import React from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import Text from '@/atoms/Text';

export type ChartType = 'bar' | 'stackedBar' | 'rangeBar';

export interface ChartSeriesConfig<T> {
  dataKey: string | ((row: T) => unknown);
  name: string;
  color: string;
  radius?: [number, number, number, number];
  id?: string;
}

export interface ChartReferenceLineConfig {
  axis: 'x' | 'y';
  value: number;
  color?: string;
  label?: string;
  /** Rendered in the same styled tooltip used for data points, shown only on hover. Ignored when `label` is set. */
  hoverContent?: React.ReactNode;
  strokeWidth?: number;
  dashed?: boolean;
}

export type ChartXAxisConfig = React.ComponentProps<typeof XAxis>;
export type ChartYAxisConfig = React.ComponentProps<typeof YAxis>;

export interface ChartTooltipPayload<T> {
  payload?: T;
  name?: string | number;
  dataKey?: string | number | ((row: T) => unknown);
  seriesId?: string;
  segmentData?: unknown;
}

export interface ChartTooltipProps<T> {
  active?: boolean;
  payload?: ChartTooltipPayload<T>[];
}

export interface ChartProps<T> {
  type: ChartType;
  data: T[];
  series: ChartSeriesConfig<T>[];
  xAxis?: ChartXAxisConfig;
  yAxis?: ChartYAxisConfig;
  legend?: boolean;
  tooltip?: (props: ChartTooltipProps<T>) => React.ReactNode;
  referenceLines?: ChartReferenceLineConfig[];
  height?: number;
  barSize?: number;
  barGap?: number;
  emptyMessage?: React.ReactNode;
  getRowColor?: (row: T, series: ChartSeriesConfig<T>) => string | undefined;
}

export function Chart<T>({
  type,
  data,
  series,
  xAxis,
  yAxis,
  legend = false,
  tooltip,
  referenceLines = [],
  height = 300,
  barSize,
  barGap,
  emptyMessage,
  getRowColor,
}: Readonly<ChartProps<T>>): React.ReactElement | null {
  const [hoveredRange, setHoveredRange] = React.useState<{
    row: T;
    seriesId: string;
    segmentData?: unknown;
    x: number;
    y: number;
  } | null>(null);

  const [hoveredReferenceLine, setHoveredReferenceLine] = React.useState<{
    content: React.ReactNode;
    x: number;
    y: number;
  } | null>(null);

  const chartContainerRef = React.useRef<HTMLDivElement>(null);

  /** Invisible hit area along a reference line; hovering it shows `content` in the shared tooltip style. */
  const renderReferenceLineHover =
    (content: React.ReactNode) =>
    ({ viewBox }: { viewBox?: { x?: number; y?: number; height?: number } }) => {
      const moveHandler = (event: React.MouseEvent<SVGRectElement>) => {
        const container = chartContainerRef.current;

        if (!container) {
          return;
        }

        const rect = container.getBoundingClientRect();

        // The reference line sits on top of data points when they overlap; clearing
        // hoveredRange here prevents both tooltips from rendering at the same time.
        setHoveredRange(null);
        setHoveredReferenceLine({
          content,
          x: event.clientX - rect.left + 10,
          y: event.clientY - rect.top + 10,
        });
      };

      return (
        <rect
          x={(viewBox?.x ?? 0) - 6}
          y={viewBox?.y ?? 0}
          width={12}
          height={viewBox?.height ?? 0}
          fill="transparent"
          onMouseEnter={moveHandler}
          onMouseMove={moveHandler}
          onMouseLeave={() => setHoveredReferenceLine(null)}
        />
      );
    };

  if (!data.length) {
    return emptyMessage ? (
      <Text variant="bodySmall" color="var(--text-secondary)">
        {emptyMessage}
      </Text>
    ) : null;
  }

  const isRangeLayout = type === 'rangeBar';
  const isStacked = type === 'stackedBar';

  /*
   * rangeBar expects a numeric [start, end] domain.
   *
   * The allocation timeline already supplies this through xAxis.domain.
   */
  const rangeDomain = Array.isArray(xAxis?.domain) ? xAxis.domain : undefined;

  const domainStart =
    rangeDomain && typeof rangeDomain[0] === 'number' ? rangeDomain[0] : undefined;

  const domainEnd =
    rangeDomain && typeof rangeDomain[rangeDomain.length - 1] === 'number'
      ? rangeDomain[rangeDomain.length - 1]
      : undefined;

  /*
   * Custom range shape.
   *
   * Recharts gives us ONE Y position for the user.
   * We then draw every allocation rectangle using that
   * exact same y/height.
   */
  const RangeBarShape = ({
    x = 0,
    y = 0,
    width = 0,
    height: shapeHeight = barSize ?? 8,
    payload,
  }: {
    x?: number;
    y?: number;
    width?: number;
    height?: number;
    payload?: T;
  }): React.ReactElement | null => {
    if (
      !payload ||
      domainStart === undefined ||
      domainEnd === undefined ||
      domainEnd <= domainStart
    ) {
      return null;
    }

    const row = payload as T & {
      segments?: Array<{
        key: string;
        allocationStartDate: string;
        allocationEndDate: string;
      }>;
    };

    if (!row.segments?.length) {
      return null;
    }

    const toX = (timestamp: number): number => {
      const clamped = Math.max(domainStart, Math.min(domainEnd, timestamp));

      return x + ((clamped - domainStart) / (domainEnd - domainStart)) * width;
    };

    return (
      <g>
        {row.segments.map((segment) => {
          const start = new Date(segment.allocationStartDate).getTime();

          const end = new Date(segment.allocationEndDate).getTime();

          const segmentX = toX(start);
          const segmentEndX = toX(end);

          const segmentWidth = Math.max(0, segmentEndX - segmentX);

          const matchingSeries = series.find((item) => item.id === segment.key);

          const color = matchingSeries?.color ?? series[0]?.color ?? 'var(--color-accent-500)';

          return (
            <rect
              key={segment.key}
              x={segmentX}
              y={y}
              width={segmentWidth}
              height={shapeHeight}
              rx={4}
              ry={4}
              fill={color}
              onMouseEnter={(event) => {
                const container = chartContainerRef.current;

                if (!container) {
                  return;
                }

                const rect = container.getBoundingClientRect();

                setHoveredReferenceLine(null);
                setHoveredRange({
                  row: payload,
                  seriesId: segment.key,
                  segmentData: segment,
                  x: event.clientX - rect.left + 10,
                  y: event.clientY - rect.top + 10,
                });
              }}
              onMouseMove={(event) => {
                const container = chartContainerRef.current;

                if (!container) {
                  return;
                }

                const rect = container.getBoundingClientRect();

                setHoveredReferenceLine(null);
                setHoveredRange({
                  row: payload,
                  seriesId: segment.key,
                  segmentData: segment,
                  x: event.clientX - rect.left + 10,
                  y: event.clientY - rect.top + 10,
                });
              }}
              onMouseLeave={() => {
                setHoveredRange(null);
              }}
            />
          );
        })}
      </g>
    );
  };

  return (
    <div
      ref={chartContainerRef}
      style={{
        position: 'relative',
        width: '100%',
        height,
      }}
      onMouseLeave={() => {
        if (isRangeLayout) {
          setHoveredRange(null);
        }
        setHoveredReferenceLine(null);
      }}
    >
      <ResponsiveContainer width="100%" height={height}>
        <BarChart
          data={data}
          layout={isRangeLayout ? 'vertical' : 'horizontal'}
          barGap={barGap}
          margin={{
            top: 16,
            right: 16,
            bottom: 0,
            left: 0,
          }}
        >
          <CartesianGrid
            horizontal={!isRangeLayout}
            vertical={isRangeLayout}
            stroke="var(--border-muted)"
          />

          <XAxis
            tick={{
              fill: 'var(--text-secondary)',
              fontSize: 12,
            }}
            axisLine={{
              stroke: 'var(--border-muted)',
            }}
            tickLine={false}
            {...xAxis}
          />

          <YAxis
            tick={{
              fill: 'var(--text-secondary)',
              fontSize: 12,
            }}
            axisLine={false}
            tickLine={false}
            width={48}
            {...yAxis}
          />

          {referenceLines.map((line) => (
            <ReferenceLine
              key={`${line.axis}-${line.value}`}
              {...(line.axis === 'y' ? { y: line.value } : { x: line.value })}
              stroke={line.color ?? 'var(--border-default)'}
              strokeWidth={line.strokeWidth}
              strokeDasharray={line.dashed === false ? undefined : '4 4'}
              label={
                line.label
                  ? {
                      value: line.label,
                      position: 'right',
                      fill: line.color,
                      fontSize: 12,
                    }
                  : line.hoverContent
                    ? renderReferenceLineHover(line.hoverContent)
                    : undefined
              }
            />
          ))}

          {/*
           * IMPORTANT:
           *
           * shared={false} makes Recharts return the
           * specific bar being hovered instead of all
           * bars for the same X-axis category.
           *
           * This is important for UserUtilizationChart,
           * where Allocation 1 and Allocation 2 belong
           * to the same user.
           *
           * This block is ONLY for normal bar/stackedBar.
           * rangeBar continues to use the custom tooltip
           * below.
           */}
          {!isRangeLayout && tooltip && (
            <Tooltip
              shared={false}
              content={(props) => {
                const enrichedPayload = (props.payload ?? []).map((item) => {
                  /*
                   * Match the Recharts tooltip item back
                   * to the Chart series.
                   *
                   * name is the safest match because the
                   * Bar below explicitly receives s.name.
                   *
                   * dataKey is kept as a fallback.
                   */
                  const matchingSeries = series.find(
                    (chartSeries) =>
                      chartSeries.name === item.name || chartSeries.dataKey === item.dataKey,
                  );

                  return {
                    ...item,
                    seriesId: matchingSeries?.id,
                  };
                });

                return tooltip({
                  ...props,
                  payload: enrichedPayload,
                });
              }}
              cursor={{
                fill: 'var(--bg-subtle)',
              }}
            />
          )}

          {legend && (
            <Legend
              iconType="circle"
              wrapperStyle={{
                fontSize: 12,
                color: 'var(--text-secondary)',
              }}
            />
          )}

          {isRangeLayout ? (
            /*
             * IMPORTANT:
             *
             * There is ONLY ONE Bar.
             *
             * Therefore Recharts creates exactly ONE Y lane
             * for each user.
             *
             * The custom shape draws all allocations inside
             * that same lane.
             */
            <Bar
              dataKey={() => [domainStart ?? 0, domainEnd ?? 1]}
              name="Allocation"
              fill="transparent"
              barSize={barSize ?? 10}
              legendType="none"
              shape={<RangeBarShape />}
              isAnimationActive={false}
            />
          ) : (
            series.map((s, idx) => (
              <Bar
                key={typeof s.dataKey === 'string' ? s.dataKey : idx}
                dataKey={s.dataKey as never}
                name={s.name}
                fill={s.color}
                stackId={isStacked ? 'stack' : undefined}
                stroke={isStacked ? 'var(--bg-surface)' : undefined}
                strokeWidth={isStacked ? 2 : undefined}
                radius={s.radius}
                maxBarSize={barSize ?? 56}
              >
                {getRowColor &&
                  data.map((row, rowIdx) => (
                    <Cell key={rowIdx} fill={getRowColor(row, s) ?? s.color} />
                  ))}
              </Bar>
            ))
          )}
        </BarChart>
      </ResponsiveContainer>

      {/* Custom tooltip for rangeBar (mutually exclusive with the reference-line tooltip below) */}
      {isRangeLayout && tooltip && hoveredRange && !hoveredReferenceLine && (
        <div
          style={{
            position: 'absolute',
            left: hoveredRange.x,
            top: hoveredRange.y,
            zIndex: 1000,
            pointerEvents: 'none',
          }}
        >
          {tooltip({
            active: true,
            payload: [
              {
                payload: hoveredRange.row,
                seriesId: hoveredRange.seriesId,
                segmentData: hoveredRange.segmentData,
              },
            ],
          })}
        </div>
      )}

      {/* Shared-style tooltip for reference lines (e.g. the "today" marker) */}
      {hoveredReferenceLine && (
        <div
          style={{
            position: 'absolute',
            left: hoveredReferenceLine.x,
            top: hoveredReferenceLine.y,
            zIndex: 1000,
            pointerEvents: 'none',
          }}
        >
          {hoveredReferenceLine.content}
        </div>
      )}
    </div>
  );
}

export default Chart;
